const DARK_TEXT = '#111827';
const LIGHT_TEXT = '#ffffff';

const clampByte = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

/** Parse #rgb / #rrggbb / rgb() / rgba() into sRGB channels. */
export const parseColour = (
  colour: string
): { r: number; g: number; b: number } | null => {
  const raw = (colour || '').trim();
  if (!raw) return null;

  const rgbMatch = raw.match(
    /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*[0-9.]+\s*)?\)$/i
  );
  if (rgbMatch) {
    return {
      r: clampByte(Number(rgbMatch[1])),
      g: clampByte(Number(rgbMatch[2])),
      b: clampByte(Number(rgbMatch[3])),
    };
  }

  const hex = raw.replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

const luminance = (rgb: { r: number; g: number; b: number }) =>
  0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);

const contrast = (l1: number, l2: number) => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Pick white or slate text for readable contrast on the pill background.
 * Prefer dark text on mid/light fills (relative luminance ≥ 0.45) so pastel
 * blues/yellows never force white-on-light.
 */
export const jobLabelTextColor = (colour?: string | null): string => {
  const bg = parseColour(colour || '');
  if (!bg) return DARK_TEXT;
  const bgL = luminance(bg);
  if (bgL >= 0.45) {
    return DARK_TEXT;
  }
  const darkContrast = contrast(bgL, luminance({ r: 17, g: 24, b: 39 }));
  const lightContrast = contrast(bgL, luminance({ r: 255, g: 255, b: 255 }));
  if (lightContrast >= 4.5 && lightContrast >= darkContrast) {
    return LIGHT_TEXT;
  }
  if (darkContrast >= 4.5) {
    return DARK_TEXT;
  }
  return lightContrast >= darkContrast ? LIGHT_TEXT : DARK_TEXT;
};

export const buildJobLabelText = (
  clientName?: string | null,
  projectName?: string | null
): string => {
  const client = (clientName || '').trim();
  const project = (projectName || '').trim();
  if (client && project) {
    return `${client} – ${project}`.toUpperCase();
  }
  return (client || project || '—').toUpperCase();
};
